import { useListContext } from "ra-core";
import { Separator } from "@/components/ui/separator";
import { NOMAD_STATES, US_STATES, isCertificateValid } from "./constants";

type Certificate = {
  id: number;
  valid_states: string[];
  expiry_date: string;
  reviewed: boolean;
};

type StateStatus = "available" | "restricted" | "nomad";

const statusConfig: Record<
  StateStatus,
  { label: string; className: string; dotClass: string }
> = {
  available: {
    label: "Available",
    className: "bg-green-50 border-green-200 text-green-800 dark:bg-green-950 dark:border-green-800 dark:text-green-200",
    dotClass: "bg-green-500",
  },
  nomad: {
    label: "No Cert. Required",
    className: "bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-950 dark:border-blue-800 dark:text-blue-200",
    dotClass: "bg-blue-400",
  },
  restricted: {
    label: "Restricted",
    className: "bg-red-50 border-red-200 text-red-800 dark:bg-red-950 dark:border-red-800 dark:text-red-200",
    dotClass: "bg-red-500",
  },
};

const StateChip = ({ code, status }: { code: string; status: StateStatus }) => {
  const cfg = statusConfig[status];
  return (
    <span
      className={`inline-flex items-center gap-1 text-xs px-2 py-1 rounded border font-medium ${cfg.className}`}
      title={status === "nomad" ? "No resale certificate required" : status}
    >
      <span className={`h-1.5 w-1.5 rounded-full shrink-0 ${cfg.dotClass}`} />
      {code}
    </span>
  );
};

export const ResaleStatePanel = () => {
  const { data: certificates, isPending } = useListContext<Certificate>();

  if (isPending) return null;

  // Collect all states covered by at least one valid certificate
  const coveredStates = new Set<string>();
  for (const cert of certificates ?? []) {
    if (isCertificateValid(cert)) {
      for (const state of cert.valid_states ?? []) {
        coveredStates.add(state);
      }
    }
  }

  const getStatus = (code: string): StateStatus => {
    if (NOMAD_STATES.includes(code as any)) return "nomad";
    if (coveredStates.has(code)) return "available";
    return "restricted";
  };

  const available = US_STATES.filter((s) => getStatus(s.value) === "available");
  const nomad = US_STATES.filter((s) => getStatus(s.value) === "nomad");
  const restricted = US_STATES.filter((s) => getStatus(s.value) === "restricted");

  return (
    <div className="flex flex-col gap-4 text-sm">
      <div className="flex gap-4 text-xs text-muted-foreground">
        <span className="flex items-center gap-1">
          <span className="h-2 w-2 rounded-full bg-green-500" />
          {available.length} available
        </span>
        <span className="flex items-center gap-1">
          <span className="h-2 w-2 rounded-full bg-blue-400" />
          {nomad.length} no cert. required
        </span>
        <span className="flex items-center gap-1">
          <span className="h-2 w-2 rounded-full bg-red-500" />
          {restricted.length} restricted
        </span>
      </div>

      {available.length > 0 && (
        <div className="flex flex-col gap-2">
          <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            Available ({available.length})
          </h4>
          <div className="flex flex-wrap gap-1.5">
            {available.map((s) => (
              <StateChip key={s.value} code={s.value} status="available" />
            ))}
          </div>
        </div>
      )}

      {nomad.length > 0 && (
        <>
          {available.length > 0 && <Separator />}
          <div className="flex flex-col gap-2">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              No Certificate Required — NOMAD ({nomad.length})
            </h4>
            <div className="flex flex-wrap gap-1.5">
              {nomad.map((s) => (
                <StateChip key={s.value} code={s.value} status="nomad" />
              ))}
            </div>
          </div>
        </>
      )}

      {restricted.length > 0 && (
        <>
          {(available.length > 0 || nomad.length > 0) && <Separator />}
          <div className="flex flex-col gap-2">
            <h4 className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
              Restricted — Certificate Required ({restricted.length})
            </h4>
            <div className="flex flex-wrap gap-1.5">
              {restricted.map((s) => (
                <StateChip key={s.value} code={s.value} status="restricted" />
              ))}
            </div>
          </div>
        </>
      )}
    </div>
  );
};
